import "./main.css";
import "alurkerja-ui/dist/style.css";
import "../node_modules/jspreadsheet-ce/dist/jspreadsheet.css";

import "./e2e.d.ts";
import "./rebuild.d.ts";

import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  RouterProvider,
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import {
  AuthContext as AlurkerjaAxiosContext,
  ThemeContext as AlurkerjaThemeContext,
} from "alurkerja-ui";
import { alurkerjaThemeConfig } from "./alurkerja-theme-config";
import { QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./contexts/themeProvider";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { queryClient } from "./utils/lib/queryClient";
import { axiosInstance } from "./utils/lib/axios";
import { routes } from "./routes";
import { AuthProvider } from "./features/auth/authProvider";

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const EXTRA_KEY = "ROUTE_TO";
const transport = Sentry.makeMultiplexedTransport(
  Sentry.makeFetchTransport,
  (args) => {
    const event = args.getEvent();
    if (
      event &&
      event.extra &&
      EXTRA_KEY in event.extra &&
      Array.isArray(event.extra[EXTRA_KEY])
    ) {
      return event.extra[EXTRA_KEY];
    }
    return [];
  },
);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.moduleMetadataIntegration(),
    ],
    transport,
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      import.meta.env.VITE_SENTRY_TRACING_TARGET,
      import.meta.env.VITE_API_BASE,
    ],
    beforeSend: (event) => {
      if (event?.exception?.values?.[0]?.stacktrace?.frames) {
        const frames = event.exception.values[0].stacktrace.frames;
        // Find the last frame with module metadata containing a DSN
        const routeTo = frames
          .filter((frame) => frame.module_metadata && frame.module_metadata.dsn)
          .map((v) => v.module_metadata)
          .slice(-1); // using top frame only - you may want to customize this according to your needs
        if (routeTo.length) {
          event.extra = {
            ...event.extra,
            [EXTRA_KEY]: routeTo,
          };
        }
      }
      return event;
    },
  });
}

const createRouter = sentryDsn
  ? Sentry.wrapCreateBrowserRouter(createBrowserRouter)
  : createBrowserRouter;
const router = createRouter(routes);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AuthProvider>
      <AlurkerjaAxiosContext.Provider value={axiosInstance}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <AlurkerjaThemeContext.Provider value={alurkerjaThemeConfig}>
              <RouterProvider router={router} />
            </AlurkerjaThemeContext.Provider>
          </ThemeProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </AlurkerjaAxiosContext.Provider>
    </AuthProvider>
  </React.StrictMode>,
);
